import { useEffect, useState } from 'react'
import { useTeamsSDK } from 'use-teams-sdk'

type Theme = 'light' | 'dark' | 'contrast' | 'default'

export function useTeamsTheme(): Theme {
  const [theme, setTheme] = useState<Theme>('default')
  const teamsSDKPromise = useTeamsSDK()

  useEffect(() => {
    // Only run client-side
    if (typeof window === 'undefined') return

    // Check system preference first
    let defaultTheme: Theme = 'light'
    try {
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      defaultTheme = prefersDark ? 'dark' : 'light'
    } catch (e) {
      console.error('Error checking media query:', e)
    }

    // Set initial theme based on system preference
    setTheme(defaultTheme)

    // Listen for system preference changes
    let mediaQuery: MediaQueryList | null = null
    try {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        setTheme(e.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
    } catch (e) {
      console.error('Error setting up media query listener:', e)
    }

    // Try to get Teams theme, which will override system preference if available
    async function detectTeamsTheme() {
      try {
        const teamsSDK = await teamsSDKPromise
        if (teamsSDK) {
          const context = await teamsSDK.app.getContext()
          if (context.app?.theme) {
            setTheme(context.app.theme as Theme)
          }
        }
      } catch (error) {
        console.error('Failed to get Teams theme:', error)
      }
    }

    detectTeamsTheme()

    return () => {
      if (mediaQuery) {
        try {
          const handleChange = (e: MediaQueryListEvent) => {
            setTheme(e.matches ? 'dark' : 'light')
          }
          mediaQuery.removeEventListener('change', handleChange)
        } catch (e) {
          console.error('Error removing media query listener:', e)
        }
      }
    }
  }, [teamsSDKPromise])

  return theme
}
